import {useNavigate, useParams} from "react-router-dom";
import {Box, Chip, CircularProgress, IconButton, Typography} from "@mui/material";
import {
    ArrowForward,
    ArrowBack,
    Edit as EditIcon,
    Assessment as OverviewIcon,
    HourglassEmpty as InProgressIcon,
} from "@mui/icons-material";
import {useEffect, useState} from "react";
import {useMessageContext} from "../contexts/MessageContext";
import {getPlan, getWeekStatistics} from "../services/PlanSerice";
import {getErrorMessage} from "../enums/ErrorMessages";
import {colors, borders} from "../componenets/ui/colors";
import {RTL} from "../cache/CacheRtl";

import WeekOverviewStats from "../componenets/client/week-overview/WeekOverviewStats";
import VolumeHistoryChart from "../componenets/client/week-overview/VolumeHistoryChart";
import ExerciseComparisonTable from "../componenets/client/week-overview/ExerciseComparisonTable";
import PersonalRecordsCards from "../componenets/client/week-overview/PersonalRecordsCards";
import MuscleBreakdownChart from "../componenets/client/week-overview/MuscleBreakdownChart";
import {workoutDayLabel} from "../services/WorkoutDayNameService";

const ClientWeekOverviewPage = () => {
    const [planState, setPlanState] = useState(null);
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);

    const {notificationHandler} = useMessageContext();
    const params = useParams();
    const navigate = useNavigate();

    const [blockIdx, setBlockIdx] = useState(Number(params.blockIndex));
    const [weekIdx, setWeekIdx] = useState(Number(params.weekIndex));

    // Fetch plan on mount
    useEffect(() => {
        getPlan(params.userId, params.programId)
            .then((res) => setPlanState(res.data))
            .catch((e) =>
                notificationHandler.error(getErrorMessage(e.response?.data?.errorCode))
            );
    }, []);

    // Fetch statistics when plan or week changes
    useEffect(() => {
        if (!planState?.blocks) return;

        const blocks = [...planState.blocks].sort((a, b) => a.index - b.index);
        const curBlock = blocks[blockIdx];
        if (!curBlock?.weeks) return;

        const weeks = [...curBlock.weeks].sort((a, b) => a.index - b.index);
        const curWeek = weeks[weekIdx];
        if (!curWeek?.id) return;

        setLoading(true);
        getWeekStatistics(curWeek.id)
            .then((res) => setStatistics(res.data.body ?? res.data))
            .catch((e) =>
                notificationHandler.error(
                    getErrorMessage(e.response?.data?.errorCode) || "שגיאה בטעינת נתונים"
                )
            )
            .finally(() => setLoading(false));
    }, [planState, blockIdx, weekIdx]);

    // Navigation helpers
    const handleForward = () => {
        if (!planState?.blocks) return;
        const blocks = [...planState.blocks].sort((a, b) => a.index - b.index);
        const curBlock = blocks[blockIdx];
        if (!curBlock?.weeks) return;

        if (weekIdx < curBlock.weeks.length - 1) {
            setWeekIdx((prev) => prev + 1);
        } else if (blockIdx < blocks.length - 1) {
            setBlockIdx((prev) => prev + 1);
            setWeekIdx(0);
        }
    };

    const handleBackwards = () => {
        if (!planState?.blocks) return;
        const blocks = [...planState.blocks].sort((a, b) => a.index - b.index);

        if (weekIdx > 0) {
            setWeekIdx((prev) => prev - 1);
        } else if (blockIdx > 0) {
            const prevBlock = blocks[blockIdx - 1];
            setBlockIdx((prev) => prev - 1);
            setWeekIdx(prevBlock.weeks.length - 1);
        }
    };

    // Get current week data
    const getCurrentWeek = () => {
        if (!planState?.blocks) return null;
        const blocks = [...planState.blocks].sort((a, b) => a.index - b.index);
        const curBlock = blocks[blockIdx];
        if (!curBlock?.weeks) return null;
        const weeks = [...curBlock.weeks].sort((a, b) => a.index - b.index);
        return weeks[weekIdx] || null;
    };

    const currentWeek = getCurrentWeek();
    const workouts = currentWeek?.workouts?.sort((a, b) => a.index - b.index) || [];
    const weekFinished = currentWeek?.finished === true;
    const completedWorkouts = workouts.filter((w) => w.stateType === "COMPLETED").length;
    const hasAnyCompleted = completedWorkouts > 0;

    const statusColors = {
        COMPLETED: {bg: colors.successBg, text: colors.successText, label: "הושלם"},
        IN_PROGRESS: {bg: colors.warningBg, text: colors.warningText, label: "בתהליך"},
        PENDING: {bg: colors.bgHover, text: colors.textMuted, label: "ממתין"},
    };

    return (
        <Box sx={{padding: {xs: "12px", md: "20px"}, direction: "rtl", maxWidth: "1200px", margin: "0 auto"}}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: {xs: 1, md: 2}}}>
                    <IconButton
                        onClick={() => navigate(`/app/clients/${params.userId}`)}
                        sx={{
                            color: colors.textSecondary,
                            padding: {xs: "6px", md: "8px"},
                            "&:hover": {backgroundColor: colors.bgHover, color: colors.textPrimary},
                        }}
                    >
                        <ArrowForward sx={{fontSize: {xs: "20px", md: "24px"}}}/>
                    </IconButton>
                    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                        <OverviewIcon sx={{color: colors.primary, fontSize: {xs: "20px", md: "24px"}}}/>
                        <Typography
                            sx={{
                                fontSize: {xs: "18px", md: "22px"},
                                fontWeight: 700,
                                color: colors.textPrimary,
                            }}
                        >
                            סקירת שבוע
                        </Typography>
                    </Box>
                </Box>

                <RTL>
                    <IconButton
                        onClick={() =>
                            navigate(
                                `/app/clients/${params.userId}/${params.programId}/week/${blockIdx}/${weekIdx}`
                            )
                        }
                        sx={{
                            color: colors.textSecondary,
                            border: borders.default,
                            borderRadius: borders.radius.md,
                            padding: "6px 12px",
                            gap: 0.5,
                            "&:hover": {backgroundColor: colors.bgHover, color: colors.textPrimary},
                        }}
                    >
                        <EditIcon sx={{fontSize: "18px"}}/>
                        <Typography sx={{fontSize: "13px", fontWeight: 600}}>עריכה</Typography>
                    </IconButton>
                </RTL>
            </Box>

            {/* Week Navigation */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: {xs: "12px", md: "16px"},
                    mb: 3,
                    padding: {xs: "8px 0", md: "10px 0"},
                }}
            >
                <IconButton
                    onClick={handleBackwards}
                    sx={{
                        color: colors.textSecondary,
                        padding: {xs: "6px", md: "8px"},
                        backgroundColor: colors.bg,
                        border: borders.default,
                        borderRadius: borders.radius.md,
                        "&:hover": {backgroundColor: colors.bgHover, color: colors.textPrimary},
                    }}
                >
                    <ArrowForward sx={{fontSize: {xs: "18px", md: "20px"}}}/>
                </IconButton>

                <Typography
                    sx={{
                        fontSize: {xs: "14px", md: "16px"},
                        fontWeight: 600,
                        color: colors.textPrimary,
                        minWidth: {xs: "120px", md: "150px"},
                        textAlign: "center",
                    }}
                >
                    בלוק {blockIdx + 1} / שבוע {weekIdx + 1}
                </Typography>

                <IconButton
                    onClick={handleForward}
                    sx={{
                        color: colors.textSecondary,
                        padding: {xs: "6px", md: "8px"},
                        backgroundColor: colors.bg,
                        border: borders.default,
                        borderRadius: borders.radius.md,
                        "&:hover": {backgroundColor: colors.bgHover, color: colors.textPrimary},
                    }}
                >
                    <ArrowBack sx={{fontSize: {xs: "18px", md: "20px"}}}/>
                </IconButton>
            </Box>

            {/* Loading State */}
            {loading && (
                <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400}}>
                    <CircularProgress sx={{color: colors.primary}}/>
                </Box>
            )}

            {/* Content */}
            {!loading && statistics && (
                <Box sx={{display: "flex", flexDirection: "column", gap: 3}}>
                    {/* In-Progress Banner */}
                    {!weekFinished && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                padding: "14px 20px",
                                backgroundColor: hasAnyCompleted ? colors.warningBg : colors.infoBg,
                                border: `1px solid ${hasAnyCompleted ? colors.warning : colors.info}33`,
                                borderRadius: borders.radius.lg,
                            }}
                        >
                            <InProgressIcon sx={{color: hasAnyCompleted ? colors.warning : colors.info, fontSize: 22}}/>
                            <Box>
                                <Typography sx={{fontSize: "14px", fontWeight: 600, color: colors.textPrimary}}>
                                    {hasAnyCompleted
                                        ? `השבוע עדיין בתהליך — ${completedWorkouts} מתוך ${workouts.length} אימונים הושלמו`
                                        : "השבוע טרם התחיל — אין אימונים שהושלמו"}
                                </Typography>
                                <Typography sx={{fontSize: "12px", color: colors.textSecondary, mt: 0.25}}>
                                    {hasAnyCompleted
                                        ? "הנתונים המוצגים הם חלקיים ומבוססים על האימונים שהושלמו עד כה"
                                        : "הנתונים יוצגו לאחר השלמת אימונים"}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Workout Completion Status */}
                    {workouts.length > 0 && (
                        <Box sx={{display: "flex", gap: 1, flexWrap: "wrap"}}>
                            {workouts.map((workout, idx) => {
                                const status = statusColors[workout.stateType] || statusColors.PENDING;
                                return (
                                    <Chip
                                        key={workout.id || idx}
                                        label={`${workoutDayLabel(workout, idx + 1)} — ${status.label}`}
                                        size="small"
                                        sx={{
                                            backgroundColor: status.bg,
                                            color: status.text,
                                            fontWeight: 600,
                                            fontSize: "12px",
                                            border: `1px solid ${status.text}22`,
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    )}

                    {/* Summary Stats — always show, but pass weekFinished to control diffs */}
                    <WeekOverviewStats statistics={statistics} weekFinished={weekFinished}/>

                    {/* Detailed sections — only show if at least one workout completed */}
                    {hasAnyCompleted && (
                        <>
                            {/* Volume History Chart */}
                            <VolumeHistoryChart
                                volumeHistory={statistics.volumeHistory}
                                volumeGrowthSinceStart={weekFinished ? statistics.volumeGrowthSinceStart : null}
                            />

                            {/* Two Column Layout: Comparison + Muscle Breakdown */}
                            <Box sx={{display: "flex", gap: 3, flexDirection: {xs: "column", md: "row"}}}>
                                <Box sx={{flex: {xs: 1, md: 2}}}>
                                    <ExerciseComparisonTable comparisons={statistics.weeklyExerciseComparisons} weekFinished={weekFinished}/>
                                </Box>
                                <Box sx={{flex: {xs: 1, md: 1}}}>
                                    <MuscleBreakdownChart muscleBreakdown={statistics.muscleBreakdown}/>
                                </Box>
                            </Box>

                            {/* Personal Records — only meaningful for finished weeks */}
                            {weekFinished && (
                                <PersonalRecordsCards personalRecords={statistics.personalRecords}/>
                            )}
                        </>
                    )}
                </Box>
            )}

            {/* Empty State */}
            {!loading && !statistics && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 300,
                        gap: 2,
                        color: colors.textMuted,
                    }}
                >
                    <OverviewIcon sx={{fontSize: 48, color: colors.borderHover}}/>
                    <Typography sx={{fontSize: "16px", fontWeight: 500, color: colors.textSecondary}}>
                        אין נתונים לשבוע זה
                    </Typography>
                    <Typography sx={{fontSize: "14px", color: colors.textMuted}}>
                        נתונים יוצגו לאחר השלמת אימונים
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default ClientWeekOverviewPage;
